import { useState, useEffect, useMemo } from 'react';
import { Container, DataGrid, KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { ColumnDef } from '@tanstack/react-table';
import { NotificationTemplate } from './notification-template.types';
import { NotificationTemplateResponse } from '@/types/notification-template-management.types';
import usePagination from '@/hooks/usePagination';
import { useGetNotificationTemplatesQuery } from '@/services/NotificationTemplateService';
import { useNavigate } from 'react-router-dom';
import { formatDisplayDate } from '@/utils/Date';

const NotificationTemplateListings = () => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();

  const {
    data: templatesData,
    isLoading,
    isFetching,
    isError,
  } = useGetNotificationTemplatesQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  });

  const apiTemplates = useMemo<NotificationTemplate[]>(() => {
    const raw = templatesData?.data?.notificationTemplates ?? [];
    const mapped: NotificationTemplate[] = raw.map(
      (t: NotificationTemplateResponse) => ({
        id: String(t.id),
        name: t.name,
        title: t.title || '',
        description_en: t.description || t.description_en || '',
        description_fi: t.description_fi || '',
        description_sv: t.description_sv || '',
        description_de: t.description_de || '',
        created_at: t.created_at || t.updated_at || new Date().toISOString(),
        updated_at: t.updated_at || t.created_at || new Date().toISOString(),
      })
    );
    return mapped;
  }, [templatesData]);

  useEffect(() => {
    if (templatesData?.data) {
      const total = templatesData?.data?.totalItems ?? 0;
      const pages = templatesData?.data?.totalPages ?? 1;
      handleTotalItems(total || 0);
      handleTotalPages(pages || 1);
      setTemplates(apiTemplates);
    }
  }, [templatesData, apiTemplates, handleTotalItems, handleTotalPages]);

  const navigate = useNavigate();

  const formatName = (name: string) =>
    name
      .split(/[_-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

  const columns: ColumnDef<NotificationTemplate>[] = [
    {
      header: 'ID',
      cell: ({ row }) =>
        row.index + 1 + pagination.pageIndex * pagination.pageSize,
    },
    {
      header: 'Name',
      accessorKey: 'name',
      cell: ({ row }) => formatName(row.original.name),
    },
    {
      header: 'Title',
      accessorKey: 'title',
    },
    {
      header: 'Created At',
      accessorKey: 'created_at',
      cell: ({ row }) => formatDisplayDate(row.original.created_at),
    },
    {
      header: 'Updated At',
      accessorKey: 'updated_at',
      cell: ({ row }) => formatDisplayDate(row.original.updated_at),
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-xs btn-light"
            onClick={() =>
              navigate(`/notification-template-management/${row.original.id}`, {
                state: { template: row.original },
              })
            }
            aria-label={`View ${row.original.name}`}
          >
            <KeenIcon type="filled" icon="eye" />
          </button>
          <button
            type="button"
            className="btn btn-xs btn-light"
            onClick={() =>
              navigate(
                `/notification-template-management/${row.original.id}?mode=edit`,
                {
                  state: { template: row.original },
                }
              )
            }
            aria-label={`Edit ${row.original.name}`}
          >
            <KeenIcon type="filled" icon="pencil" className="text-gray-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle id="notification-template-management-heading" />
            <ToolbarDescription>
              You can manage your Notification templates here.
            </ToolbarDescription>
          </ToolbarHeading>
        </Toolbar>
      </Container>
      <Container>
        <div className="card">
          <div className="card-body">
            {isError ? (
              <div className="text-center py-4 text-red-600">
                Failed to load templates.
              </div>
            ) : (
              <DataGrid
                columns={columns}
                data={templates}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  totalItems: templatesData?.data?.totalItems ?? totalItems,
                  totalPages: templatesData?.data?.totalPages ?? totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                serverSide={true}
                aria-label="notification templates table"
              />
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export { NotificationTemplateListings };
